import { FeatureSelector, GeoJSONFeature, InteractionEvent, TargetFeature } from 'mapbox-gl';
import { useEffect, useRef, useState } from 'react';
import logError from '@alltrails/core/utils/logError';
import useMap from '../../hooks/useMap';
import { useAppDispatch, useAppSelector } from '../../redux';
import { selectRestingCursor } from '../../redux/customRoute';
import { updateCursor, updateIsMapFeatureSelected } from '../../redux/reducer';
import { alltrailsPoiTarget } from './constants';

export type PopupFeature = {
  name?: string | number | boolean;
  geometry: [number, number] | null;
  layerId: string | number | boolean;
  icon?: string | number | boolean;
};

// On our new base map style, we render OSM POIs on a few layers that are not part of the default Mapbox style.
// We have added additional JSON that makes some of those OSM POIs clickable and interactive using `featuresets` and `appearances`
// This hook is used to handle the interactions with these OSM POIs

const usePoiInteractions = () => {
  const map = useMap();
  const dispatch = useAppDispatch();
  const baseStyleId = useAppSelector(state => state.map.baseStyleId);
  const [popupFeature, setPopupFeature] = useState<PopupFeature | undefined>();
  const popupVisibleRef = useRef(false);

  const restingCursor = useAppSelector(selectRestingCursor);
  const restingCursorRef = useRef(restingCursor);

  useEffect(() => {
    popupVisibleRef.current = !!popupFeature;
  }, [popupFeature]);

  useEffect(() => {
    restingCursorRef.current = restingCursor;
  }, [restingCursor]);

  useEffect(() => {
    const mapboxMap = map?.getMap();
    if (!mapboxMap || baseStyleId !== 'alltrailsOutdoorsV3') {
      return;
    }

    let cancelled = false;

    const registerInteractions = () => {
      try {
        mapboxMap.addInteraction('at-poi-click', {
          type: 'click',
          target: alltrailsPoiTarget,
          handler: (e: InteractionEvent) => {
            const properties = e.feature?.properties ?? {};
            setPopupFeature({
              name: properties.name ?? null,
              geometry: [e.lngLat.lng, e.lngLat.lat],
              layerId: properties.layerId || '',
              icon: properties.icon || ''
            });
          }
        });

        // To make the OSM POI icons change size when hovered, we used the `featuresets` and `appearances` properties in the Mapbox style JSON:
        // "highlighted" is an appearance property defined on each of the layers in the alltrailsPoiTarget.featuresetId in the Mapbox style JSON
        // This style JSON was manually added to the Mapbox style JSON to make the OSM POI layers in our base map style clickable and interactive
        mapboxMap.addInteraction('at-poi-hover', {
          type: 'mouseenter',
          target: alltrailsPoiTarget,
          handler: (e: InteractionEvent) => {
            dispatch(updateCursor('pointer'));
            mapboxMap.setFeatureState(e.feature as TargetFeature | FeatureSelector | GeoJSONFeature, { highlighted: true });
            dispatch(updateIsMapFeatureSelected(true));
          }
        });

        mapboxMap.addInteraction('at-poi-unhover', {
          type: 'mouseleave',
          target: alltrailsPoiTarget,
          handler: (e: InteractionEvent) => {
            dispatch(updateCursor(restingCursorRef.current));
            mapboxMap.setFeatureState(e.feature as TargetFeature | FeatureSelector | GeoJSONFeature, { highlighted: false });
            if (!popupVisibleRef.current) {
              dispatch(updateIsMapFeatureSelected(false));
            }
          }
        });

        mapboxMap.addInteraction('at-poi-dismiss', {
          type: 'click',
          handler: () => {
            setPopupFeature(undefined);
            dispatch(updateIsMapFeatureSelected(false));
            return false;
          }
        });
      } catch (err) {
        logError(new Error('Failed to register POI interactions', { cause: err }));
      }
    };

    const onIdle = () => {
      if (cancelled) {
        return;
      }
      if (mapboxMap.isStyleLoaded()) {
        try {
          mapboxMap.removeInteraction('at-poi-click');
        } catch {}
        registerInteractions();
      }
    };

    if (mapboxMap.isStyleLoaded()) {
      registerInteractions();
    } else {
      mapboxMap.once('idle', onIdle);
    }

    return () => {
      cancelled = true;
      mapboxMap.off('idle', onIdle);
      try {
        mapboxMap.removeInteraction('at-poi-click');
        mapboxMap.removeInteraction('at-poi-hover');
        mapboxMap.removeInteraction('at-poi-unhover');
        mapboxMap.removeInteraction('at-poi-dismiss');
      } catch (e) {
        logError(new Error('Failed to remove POI interactions', { cause: e }));
      }
    };
  }, [map, baseStyleId, dispatch]);

  return { popupFeature, setPopupFeature };
};

export default usePoiInteractions;
